// CheckoutStepper — multi-step checkout progress indicator.
// Steps: Address -> Payment -> Review -> Confirm.
// Inline styles only — no Tailwind. Themed via useEmbedTokens().

import { useEmbedTokens } from "../../embed-shared/primitives";

export type CheckoutStep = "address" | "payment" | "review" | "confirm";

const STEPS: { key: CheckoutStep; label: string }[] = [
  { key: "address", label: "Address" },
  { key: "payment", label: "Payment" },
  { key: "review", label: "Review" },
  { key: "confirm", label: "Confirm" },
];

export interface CheckoutStepperProps {
  currentStep: CheckoutStep;
  completedSteps?: CheckoutStep[];
  onStepClick?: (step: CheckoutStep) => void;
}

export function CheckoutStepper({
  currentStep,
  completedSteps = [],
  onStepClick,
}: CheckoutStepperProps) {
  const t = useEmbedTokens();
  const currentIndex = STEPS.findIndex((s) => s.key === currentStep);
  const N = STEPS.length;

  // Continuous track geometry: the track spans from the first step's circle
  // center to the last step's circle center. With N equal-flex steps, each
  // circle sits at 50%/N from the left edge of its step. The track inset from
  // each side is therefore 50%/N. The filled portion extends from the first
  // circle center to the current step's circle center.
  const trackInsetPct = 50 / N; // e.g. 12.5% for N=4
  const trackTop = 11; // center of 24px circle minus 1px (2px line)
  const fillWidthPct =
    N > 1 ? (currentIndex / (N - 1)) * (100 - 100 / N) : 0;

  return (
    <nav aria-label="Checkout progress" style={{ width: "100%" }}>
      <div style={{ position: "relative" }}>
        {/* Continuous base track (pending color) */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: trackTop,
            left: `${trackInsetPct}%`,
            right: `${trackInsetPct}%`,
            height: 2,
            backgroundColor: t.colors.stepperPending,
            borderRadius: 1,
          }}
        />
        {/* Filled track overlay (complete color) — spans first circle to
            current circle so progress reads at a glance. */}
        {currentIndex > 0 && (
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: trackTop,
              left: `${trackInsetPct}%`,
              width: `${fillWidthPct}%`,
              height: 2,
              backgroundColor: t.colors.stepperComplete,
              borderRadius: 1,
            }}
          />
        )}
        <ol
          style={{
            display: "flex",
            listStyle: "none",
            margin: 0,
            padding: 0,
            gap: 0,
          }}
        >
          {STEPS.map((step, idx) => {
            const isCompleted = completedSteps.includes(step.key);
            const isCurrent = step.key === currentStep;
            const isPending = !isCompleted && !isCurrent;
            const isClickable = onStepClick && (isCompleted || idx < currentIndex);

            const circleColor = isCompleted
              ? t.colors.stepperComplete
              : isCurrent
                ? t.colors.stepperActive
                : t.colors.stepperPending;

            return (
              <li
                key={step.key}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                {/* Step circle */}
                {/* TD-319: stays raw. A 24x24 circular step indicator —
                    EmbedButton has no circular/icon size and would break
                    the stepper geometry. */}
                <button
                  type="button"
                  disabled={!isClickable}
                  onClick={() => isClickable && onStepClick?.(step.key)}
                  aria-current={isCurrent ? "step" : undefined}
                  aria-label={`Step ${idx + 1}: ${step.label}${isCompleted ? " (completed)" : isCurrent ? " (current)" : ""}`}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    backgroundColor: isPending ? t.colors.cardBg : circleColor,
                    color: isPending ? t.colors.textSecondary : t.colors.onAction,
                    boxSizing: "border-box",
                    border: isPending ? `1px solid ${t.colors.border}` : "none",
                    fontSize: t.fontSize.xs,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isClickable ? "pointer" : "default",
                    position: "relative",
                    zIndex: 1,
                    padding: 0,
                    fontFamily: t.fontFamily,
                  }}
                >
                  {isCompleted ? "✓" : idx + 1}
                </button>
                {/* Label — pending steps read as secondary text. ⚠️ AMENDED 2026-10-09
                    (Task 12, TD-575). ORIGINAL: the placeholder token (2.61:1
                    light / 3.55:1 dark). Pending circles are outlined (card
                    fill, control border, secondary-text number) and the
                    completed check uses onAction — pinned in
                    primitives/contrast.test.ts. */}
                <span
                  style={{
                    fontSize: t.fontSize.xs,
                    fontWeight: isCurrent ? 600 : 400,
                    color: isPending ? t.colors.textSecondary : t.colors.text,
                    marginTop: t.spacing.xs,
                  }}
                >
                  {step.label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
